import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'
import { useAdapter } from '@/hooks/useAdapter'
import { useAppView } from '@/hooks/useAppView'
import { accountQueryIdentifier } from '@/lib/accountQueryIdentifier'

interface UseNewPostsDetectionOptions {
    currentNewestAt?: string
    feedUri?: string
    enabled?: boolean
}

export function useNewPostsDetection({
    currentNewestAt,
    feedUri,
    enabled = true,
}: UseNewPostsDetectionOptions) {
    const { session, mastodonSession, pixelfedSession, nostrSession } = useAuth()
    const { selectedProtocol } = usePlatform()
    const { adapter, session: adapterSession } = useAdapter()
    const { appView, sessionAgent } = useAppView()

    const [hasNewPosts, setHasNewPosts] = useState(false)

    // Reset hasNewPosts when the feed refreshes (currentNewestAt changes)
    const prevNewestAtRef = useRef(currentNewestAt)
    useEffect(() => {
        if (prevNewestAtRef.current !== currentNewestAt) {
            prevNewestAtRef.current = currentNewestAt
            setHasNewPosts(false)
        }
    }, [currentNewestAt])

    const identifier =
        accountQueryIdentifier(selectedProtocol, {
            session,
            mastodonSession,
            pixelfedSession,
            nostrSession,
        }) ?? undefined

    const pollEnabled = enabled

    const { data: latestPostAt } = useQuery<string | null>({
        queryKey: ['newPostsCheck', selectedProtocol, identifier, feedUri ?? 'following', appView],
        queryFn: async () => {
            if (!adapterSession) throw new Error('Session unavailable')
            const page = await adapter.fetchTimeline(adapterSession, {
                feedUri,
                limit: 1,
                sessionAgent,
            })
            return page.posts[0]?.createdAt ?? null
        },
        enabled: pollEnabled && !!adapterSession,
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        staleTime: 0,
        gcTime: 0,
    })

    // Detect new posts by comparing fetched timestamp against current feed.
    // Including currentNewestAt in deps is safe: equal timestamps won't re-trigger,
    // and the reset effect above clears hasNewPosts before this can re-fire on refresh.
    useEffect(() => {
        if (!latestPostAt || !currentNewestAt) return
        if (new Date(latestPostAt) > new Date(currentNewestAt)) {
            setHasNewPosts(true)
        }
    }, [latestPostAt, currentNewestAt])

    const dismiss = () => setHasNewPosts(false)

    return { hasNewPosts, dismiss }
}
